/**
 * search_connectors tool - Find connectors by name or category
 */

import { z } from "zod";
import type { SchemaCache } from "../cache/schema-cache.js";
import type { ConnectorMetadataApi } from "../api/connector-metadata.js";
import { logger } from "../utils/logger.js";

export const searchConnectorsInputSchema = z.object({
  query: z
    .string()
    .min(1)
    .describe("Search term to find connectors (e.g., 'SharePoint', 'Teams', 'SQL')"),
  tier: z
    .enum(["Standard", "Premium", "all"])
    .optional()
    .describe("Filter by connector tier (Standard, Premium, or all)"),
  limit: z
    .number()
    .min(1)
    .max(50)
    .optional()
    .describe("Maximum number of results (default 20)"),
  useCache: z
    .boolean()
    .optional()
    .describe("Use cached connector data if available (default true)"),
});

export type SearchConnectorsInput = z.infer<typeof searchConnectorsInputSchema>;

export const searchConnectorsTool = {
  name: "search_connectors",
  description:
    "Search for Power Automate connectors by name, description, or category. Returns matching connectors with their tier (Standard/Premium) and capabilities. Use this to discover available connectors before building flows.",
  inputSchema: {
    type: "object" as const,
    properties: {
      query: {
        type: "string",
        description: "Search term to find connectors (e.g., 'SharePoint', 'Teams', 'SQL')",
      },
      tier: {
        type: "string",
        enum: ["Standard", "Premium", "all"],
        description: "Filter by connector tier (Standard, Premium, or all)",
      },
      limit: {
        type: "number",
        description: "Maximum number of results (default 20)",
        minimum: 1,
        maximum: 50,
      },
      useCache: {
        type: "boolean",
        description: "Use cached connector data if available (default true)",
      },
    },
    required: ["query"],
  },
};

export interface SearchConnectorsResult {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  publisher: string | null;
  tier: string;
  isCustomApi: boolean;
}

export interface SearchConnectorsContext {
  cache: SchemaCache | null;
  api: ConnectorMetadataApi;
}

export async function handleSearchConnectors(
  context: SearchConnectorsContext,
  input: unknown
): Promise<string> {
  const parsed = searchConnectorsInputSchema.parse(input);
  const limit = parsed.limit ?? 20;
  const useCache = parsed.useCache !== false;

  logger.info({ query: parsed.query, tier: parsed.tier }, "Executing search_connectors");

  let results: SearchConnectorsResult[];

  // Try cache first if enabled and available
  if (useCache && context.cache) {
    // Ask the cache for more than the page so the header can say how many
    // matched in total — a list cut silently at 20 read as "that is all".
    const cacheResults = context.cache.searchConnectors(parsed.query, Math.max(limit * 5, 200));

    if (cacheResults.length > 0) {
      results = cacheResults.map((c) => ({
        id: c.id,
        name: c.name,
        displayName: c.displayName,
        description: c.description,
        publisher: c.publisher,
        tier: c.tier,
        isCustomApi: false,
      }));

      if (parsed.tier && parsed.tier !== "all") {
        results = results.filter((r) => r.tier === parsed.tier);
      }

      const cachedTotal = results.length;
      return formatConnectorResults(results.slice(0, limit), parsed.query, cachedTotal);
    }
  }

  // Fall back to API
  const connectors = await context.api.searchConnectors(parsed.query, limit * 2);

  results = connectors.map((c) => ({
    id: c.id,
    name: c.name,
    displayName: c.properties.displayName,
    description: c.properties.description ?? null,
    publisher: c.properties.publisher ?? null,
    tier: c.properties.tier,
    isCustomApi: c.properties.isCustomApi,
  }));

  if (parsed.tier && parsed.tier !== "all") {
    results = results.filter((r) => r.tier === parsed.tier);
  }

  const totalMatches = results.length;
  results = results.slice(0, limit);

  return formatConnectorResults(results, parsed.query, totalMatches);
}

function formatConnectorResults(results: SearchConnectorsResult[], query: string, totalMatches: number = results.length): string {
  if (results.length === 0) {
    return `No connectors found for "${query}".`;
  }

  // A silent cap reads as "that is all there is". Say when the list was cut.
  const header =
    totalMatches > results.length
      ? `Found ${totalMatches} connectors for "${query}" — showing the first ${results.length} (pass limit, up to 50, or a narrower query to see the rest):\n`
      : `Found ${results.length} connector${results.length > 1 ? "s" : ""} for "${query}":\n`;
  const lines = [header];

  for (const r of results) {
    const tier = r.tier === "Premium" ? " [Premium]" : "";
    lines.push(`• ${r.displayName}${tier}`);
    lines.push(`  ID: ${r.name}`);
    if (r.description) {
      const desc = r.description.length > 80 ? r.description.slice(0, 80) + "..." : r.description;
      lines.push(`  ${desc}`);
    }
    lines.push("");
  }

  return lines.join("\n");
}
